import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import {
  CartesianGrid,
  Cell,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getAdvancedMetricSummary } from "../../../api/expenseBreakdown";
import { getSaasVendorsAnalysis } from "../../../api/saas_api/vendorAnalysis";
import { PerformanceDriverSkeleton } from "./performanceSkeletons";

import { IconButton, Popover, Switch } from "@mui/material";
import { LineChart as ReLineChart } from "recharts";

const fluid = (minPx, vw, maxPx) => `clamp(${minPx}px, ${vw}vw, ${maxPx}px)`;

const CATEGORY_BREAKDOWN_PRIMARY = "#536DEC";

const CATEGORY_BREAKDOWN_SECTORS = [
  { key: "team", label: "Team" },
  { key: "overhead", label: "Operations" },
  { key: "cogs", label: "Products" },
  { key: "marketing", label: "Marketing" },
];

const CATEGORY_BREAKDOWN_GREYS = ["#B0BEC5", "#CFD8DC", "#ECEFF1"];

const buildRoundedPercentages = (values) => {
  const safeValues = Array.isArray(values)
    ? values.map((v) => Math.abs(Number(v) || 0))
    : [];
  const total = safeValues.reduce((sum, v) => sum + v, 0);
  if (!total) return safeValues.map(() => 0);

  const raw = safeValues.map((v) => (v / total) * 100);
  const base = raw.map((p) => Math.floor(p));
  let remainder = 100 - base.reduce((sum, v) => sum + v, 0);

  const fractions = raw
    .map((p, idx) => ({
      idx,
      fraction: p - base[idx],
      value: safeValues[idx],
    }))
    .sort((a, b) => {
      if (b.fraction !== a.fraction) return b.fraction - a.fraction;
      if (b.value !== a.value) return b.value - a.value;
      return a.idx - b.idx;
    });

  for (let i = 0; i < fractions.length && remainder > 0; i += 1) {
    base[fractions[i].idx] += 1;
    remainder -= 1;
  }

  return base;
};

const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatCurrency = (value) =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatCurrencyAbs = (value) => formatCurrency(Math.abs(value || 0));

const formatPercent = (value, digits = 1) => {
  if (value == null || Number.isNaN(value)) return "0%";
  return `${value.toFixed(digits)}%`;
};

const getPercentColor = (value) => {
  if (value == null || Number.isNaN(value)) return "black";
  if (value < 0) return "#43a047";
  if (value > 0) return "#d9534f";
  return "black";
};

const donutPalette = [
  "#1e88e5",
  "#607d8b",
  "#ff9800",
  "#4caf50",
  "#9c27b0",
  "#ef5350",
  "#00acc1",
  "#8d6e63",
  "#c0ca33",
  "#5c6bc0",
];

const detailTypeForMetric = {
  team: "team",
  marketing: "marketing",
  cogs: "cogs",
  overhead: "overhead",
};

const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDateString = (dateObj) => {
  if (!dateObj) return null;
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const previousMonthEnd = (dateStr) => {
  const dateObj = parseDateString(dateStr);
  if (!dateObj) return null;
  const prevEnd = new Date(dateObj.getFullYear(), dateObj.getMonth(), 0);
  return formatDateString(prevEnd);
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const calcChangePct = (current, previous) => {
  const prev = toNumber(previous);
  const curr = toNumber(current);
  if (!prev) return curr ? 100 : 0;
  return ((curr - prev) / Math.abs(prev)) * 100;
};

const BadgePill = ({ direction = "down", text }) => {
  const arrow = direction === "up" ? "↑" : "↓";
  // In this context (expenses), "down" is good (green).
  return (
    <Box
      sx={{
        bgcolor: "#e6f9f2",
        color: "#43a047",
        height: 32,
        px: 1.25,
        py: "5px",
        borderRadius: "6px",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        fontFamily: '"Rethink Sans"',
        fontSize: fluid(15, 0.9, 18),
        fontWeight: 400,
        lineHeight: "21px",
        letterSpacing: "0.5px",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        sx={{ fontSize: fluid(16, 1.0, 20), lineHeight: 1 }}
      >
        {arrow}
      </Box>
      {text}
    </Box>
  );
};

const AlertStrip = ({ title, body, variant = "success" }) => {
  const palettes = {
    info: {
      bg: "#e5f6fd",
      title: "#1976d2",
      body: "#014361",
      icon: "rgba(2,136,209,1)",
    },
    success: {
      bg: "#edf7ed",
      title: "#388e3c",
      body: "#1e4620",
      icon: "#388e3c",
    },
  };
  const palette = variant === "info" ? palettes.info : palettes.success;

  return (
    <Box
      sx={{
        bgcolor: palette.bg,
        borderRadius: "10px",
        overflow: "hidden",
        px: 2,
        py: 1,
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.25 }}
      >
        <Typography
          sx={{
            fontFamily: '"Rethink Sans"',
            fontSize: fluid(16, 1.0, 20),
            fontWeight: 500,
            color: palette.title,
            lineHeight: 1.5,
            letterSpacing: "0.15px",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Rethink Sans"',
            fontSize: fluid(14, 0.9, 18),
            color: palette.body,
            lineHeight: 1.43,
            letterSpacing: "0.17px",
          }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
};

const LABEL_KEY_MAP = {
  Income: "income",
  Profit: "profit",
  "Marketing Spend": "marketing",
  "Team Spend": "team",
  overhead: "operations",
  cogs: "products",
};

const activeDescriptorKey = {
  "Team Costs": "team",
  "Marketing Spend": "marketing",
  "Ops Spend": "operations",
  "Product Spend": "products",
};

const COLORS = {
  team: "#536DEC",
  marketing: "#4CAF50",
  operations: "#FFB300",
  products: "#00ACC1",
  income: "#673AB7",
  profit: "#039BE5",
};

const METRIC_LABELS = {
  income: "Income",
  profit: "Profit",
  marketing: "Marketing",
  team: "Team",
  products: "Products",
  operations: "Operations",
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "8px",
        p: "12px 14px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
        border: "1px solid rgba(0,0,0,0.06)",
        minWidth: "160px",
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 500,
          mb: "8px",
        }}
      >
        {label}
      </Typography>

      <Divider sx={{ mb: "8px" }} />

      {/* Metrics */}
      {payload.map((entry) => (
        <Stack
          key={entry.dataKey}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: "4px" }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: entry.color,
            }}
          />

          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            {METRIC_LABELS[entry.dataKey] || entry.dataKey}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            ${Number(entry.value).toLocaleString()}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
};

const MonthlyBreakdownChart = (lineChart) => {
  const values = lineChart?.values;
  const activeLabel = lineChart?.label;
  const activeKey = activeDescriptorKey[activeLabel];

  const [visible, setVisible] = useState({
    team: false,
    marketing: false,
    operations: false,
    products: false,
    income: false,
    profit: false,
  });

  useEffect(() => {
    if (activeKey) {
      setVisible((prev) =>
        Object.keys(prev).reduce((acc, key) => {
          acc[key] = key === activeKey;
          return acc;
        }, {}),
      );
    }
  }, [activeKey]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const data = useMemo(() => {
    if (!Array.isArray(values) || values.length === 0) return [];

    const months = values[0]?.labels || [];

    return months.map((month, i) => {
      const row = { month };

      values.forEach((metric) => {
        const key = LABEL_KEY_MAP[metric.label];
        if (key) row[key] = metric.values?.[i] ?? 0;
      });

      return row;
    });
  }, [values]);

  const maxValue = Math.max(
    1,
    ...data.flatMap((d) =>
      Object.keys(visible)
        .filter((k) => visible[k])
        .map((k) => d[k] || 0),
    ),
  );

  const getEvenTicks = (data, labelCount = 8) => {
    if (!data?.length) return [];

    // Desired behavior:
    // - Keep the simple alternating pattern: show one month, skip one month.
    // - Do not special-case the final month; keep the pattern consistent.
    const lastIdx = data.length - 1;
    const indices = [];

    for (let i = 0; i <= lastIdx; i += 2) {
      indices.push(i);
    }

    return indices.map((idx) => data[idx]?.month).filter(Boolean);
  };

  const ticks = useMemo(() => getEvenTicks(data, 6), [data]);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "16px",
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Rethink Sans"',
            fontSize: fluid(18, 1.1, 24),
            fontWeight: 600,
            color: "rgba(0,0,0,0.87)",
            letterSpacing: "0.15px",
            lineHeight: 1.6,
          }}
        >
          Monthly Breakdown
        </Typography>

        <Box sx={{ position: "relative", display: "inline-block" }}>
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              p: 0,
              width: 32,
              height: 32,
            }}
          >
            <Box
              component="img"
              src="/filterIcon.svg"
              alt="Filter"
              sx={{
                width: "100%",
                height: "100%",
                display: "block",
              }}
            />
          </IconButton>

          <Box
            sx={{
              position: "absolute",
              top: 3,
              right: 3,
              width: 12,
              height: 12,
              borderRadius: "150px",
              bgcolor: "#536DEC",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              color: "#fff",
              fontSize: 9,
              fontWeight: 600,
              pointerEvents: "none",
            }}
          >
            {Object.values(visible).filter(Boolean).length}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: "198px",
          overflow: "visible",
          "& svg:focus, g:focus, path:focus": { outline: "none !important" },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ReLineChart data={data}>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#E5E7EB"
            />
            <Tooltip content={<ChartTooltip />} />

            <XAxis
              dataKey="month"
              ticks={ticks}
              interval={0}
              minTickGap={8}
              tickMargin={8}
              axisLine={false}
              tickLine={false}
              tick={{
                fontFamily: "Rethink Sans",
                fontSize: 12,
                fill: "#6B7280",
              }}
              padding={{ left: 20, right: 20 }}
            />

            <YAxis
              domain={[0, maxValue]}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => {
                if (value < 1000) return `$${value}`;
                const k = value / 1000;
                return `$${Number.isInteger(k) ? k : k.toFixed(1)}k`;
              }}
              tick={{
                fontFamily: "Rethink Sans",
                fontSize: 12,
                fill: "#6B7280",
              }}
            />

            {Object.keys(visible).map(
              (key) =>
                visible[key] && (
                  <Line
                    key={key}
                    type="linear"
                    dataKey={key}
                    stroke={COLORS[key]}
                    strokeWidth={3}
                    dot={false}
                  />
                ),
            )}
          </ReLineChart>
        </ResponsiveContainer>
      </Box>

      <Stack
        direction="row"
        justifyContent="center"
        mt={3}
        sx={{
          flexWrap: "wrap",
          columnGap: 3,
          rowGap: 1,
          maxWidth: "100%",
          margin: "0 auto",
        }}
      >
        {Object.keys(visible)
          .filter((key) => visible[key])
          .map((key) => (
            <Stack key={key} direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 24,
                  height: 3,
                  borderRadius: 999,
                  bgcolor: COLORS[key],
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans"',
                  fontSize: 14,
                }}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </Typography>
            </Stack>
          ))}
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              background: "#FFF",
              boxShadow:
                "0 4px 20px 0 rgba(0, 0, 0, 0.10), 0 1px 3px 0 rgba(0, 0, 0, 0.12)",
              width: "180px",
              maxWidth: "180px",
              p: 0,
            },
          },
        }}
      >
        <Box
          sx={{ display: "flex", p: "12px", width: "auto", maxWidth: "180px" }}
        >
          <Box sx={{ width: "auto", maxWidth: "180px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Box
                component="img"
                src="/IoFunnel.svg"
                alt="Filter"
                sx={{
                  width: 14,
                  height: 14,
                  display: "block",
                }}
              />

              <Typography fontWeight={600}>Filter</Typography>
            </Box>

            <Divider sx={{ my: "12px" }} />

            {Object.keys(visible).map((key) => (
              <Stack key={key} direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: COLORS[key],
                    }}
                  />
                  <Typography sx={{ fontSize: "14px" }}>
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </Typography>
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Switch
                  checked={visible[key]}
                  onChange={() =>
                    setVisible((prev) => ({
                      ...prev,
                      [key]: !prev[key],
                    }))
                  }
                />
              </Stack>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

const DonutChart = ({ slices = [] }) => {
  const totalRaw = slices.reduce((sum, s) => sum + (s.value || 0), 0);
  const hasNonZero = totalRaw > 0;

  const data = hasNonZero
    ? slices.map((s, idx) => ({
        name: s.label,
        value: s.value,
        color: s.color || donutPalette[idx % donutPalette.length],
      }))
    : [{ name: "empty", value: 1, color: "rgba(17,24,39,0.10)" }];

  const selectedIndex = Math.max(
    0,
    slices.findIndex((slice) => slice.isSelected),
  );
  const selectedSlice = slices[selectedIndex] || null;
  const selectedPercent =
    selectedSlice && Number.isFinite(Number(selectedSlice.percent))
      ? Number(selectedSlice.percent)
      : 0;

  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      sx={{ width: "100%", height: "100%" }}
    >
      <Box
        sx={{
          width: 220,
          height: 220,
          flexShrink: 0,
          position: "relative",
          "& svg:focus, g:focus, path:focus": { outline: "none !important" },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Stack spacing={0.75} sx={{ width: 106, textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans"',
                fontSize: 44,
                fontWeight: 700,
                color: "rgba(0,0,0,0.87)",
                letterSpacing: "0.25px",
                lineHeight: 1.235,
              }}
            >
              {`${selectedPercent}%`}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans"',
                fontSize: 14,
                fontWeight: 400,
                color: CATEGORY_BREAKDOWN_PRIMARY,
                letterSpacing: "0.17px",
                lineHeight: 1.43,
              }}
            >
              of total spend
            </Typography>
          </Stack>
        </Box>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={110}
              startAngle={90}
              endAngle={-270}
              padAngle={4}
              paddingAngle={4}
              stroke="none"
              cornerRadius={0}
            >
              {data.map((entry, idx) => {
                return (
                  <Cell
                    key={idx}
                    fill={entry.color}
                    innerRadius={74}
                    outerRadius={106}
                  />
                );
              })}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ flex: 1, alignSelf: "stretch", minWidth: 0 }}>
        <Stack
          spacing={1.5}
          sx={{
            height: "100%",
            justifyContent: "center",
            py: "12px",
          }}
        >
          {slices
            .map((s, idx) => (s.isSelected ? { slice: s, idx } : null))
            .filter(Boolean)
            .map(({ slice: s, idx }) => (
              <Box
                key={s.key || s.label}
                sx={{
                  bgcolor: "rgba(83,109,236,0.1)",
                  border: "1px solid rgba(83,109,236,0.5)",
                  borderRadius: "10px",
                  px: "12px",
                  py: "8px",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 999,
                        bgcolor:
                          s.color || donutPalette[idx % donutPalette.length],
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans"',
                        fontSize: fluid(13, 0.8, 16),
                        fontWeight: 600,
                        color: "#0f172b",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {s.label}
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans"',
                      fontSize: fluid(14, 0.9, 18),
                      fontWeight: 600,
                      color: "rgba(23,25,35,0.9)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatCurrency(s.value)}
                  </Typography>

                  <Chip
                    size="small"
                    label={`${Number(s.percent) || 0}%`}
                    sx={{
                      bgcolor: CATEGORY_BREAKDOWN_PRIMARY,
                      color: "#fff",
                      height: 24,
                      borderRadius: "100px",
                      "& .MuiChip-label": {
                        px: "6px",
                        fontFamily: '"Rethink Sans"',
                        fontSize: 13,
                        fontWeight: 500,
                        lineHeight: "18px",
                        letterSpacing: "0.16px",
                      },
                    }}
                  />
                </Stack>
              </Box>
            ))}

          <Divider />

          <Stack spacing={1} sx={{ width: "100%" }}>
            {slices
              .filter((s) => !s.isSelected)
              .map((s, idx) => (
                <Box
                  key={s.key || s.label}
                  sx={{
                    borderRadius: "10px",
                    px: "12px",
                    py: "8px",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ width: "100%" }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: 999,
                          bgcolor:
                            s.color || donutPalette[idx % donutPalette.length],
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans"',
                          fontSize: fluid(13, 0.8, 16),
                          fontWeight: 400,
                          color: "#0f172b",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {s.label}
                      </Typography>
                    </Stack>

                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans"',
                        fontSize: fluid(14, 0.9, 18),
                        fontWeight: 600,
                        color: "rgba(23,25,35,0.9)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatCurrency(s.value)}
                    </Typography>

                    <Chip
                      size="small"
                      label={`${Number(s.percent) || 0}%`}
                      sx={{
                        bgcolor: "rgba(0,0,0,0.08)",
                        color: "rgba(0,0,0,0.87)",
                        height: 24,
                        borderRadius: "100px",
                        "& .MuiChip-label": {
                          px: "6px",
                          fontFamily: '"Rethink Sans"',
                          fontSize: 13,
                          fontWeight: 500,
                          lineHeight: "18px",
                          letterSpacing: "0.16px",
                        },
                      }}
                    />
                  </Stack>
                </Box>
              ))}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

const DetailTable = ({ rows = [], total }) => (
  <Box
    className="performance-detail-table"
    sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: 0,
    }}
  >
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <Table size="small" sx={{ width: "100%", borderRadius: 0 }}>
        <TableHead>
          <TableRow sx={{ height: "44px", border: "none" }}>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                bgcolor: "#eef0fd",
                borderRadius: 0,
              }}
            >
              Vendor
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                bgcolor: "#eef0fd",
                width: 110,
                borderRadius: 0,
              }}
            >
              This Month
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                bgcolor: "#eef0fd",
                width: 110,
                borderRadius: 0,
              }}
            >
              Last Month
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                bgcolor: "#eef0fd",
                width: 110,
                borderRadius: 0,
              }}
            >
              % Change
            </TableCell>
          </TableRow>
        </TableHead>
      </Table>
    </Box>

    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      <Table size="small" sx={{ width: "100%", borderRadius: 0 }}>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.category}
              hover
              sx={{
                height: 44,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                "& td": { py: 0 },
              }}
            >
              <TableCell
                sx={{
                  fontFamily: '"Rethink Sans"',
                  fontSize: fluid(13, 0.8, 16),
                }}
              >
                {r.category}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontFamily: '"Rethink Sans"',
                  fontSize: fluid(13, 0.8, 16),
                  width: 110,
                }}
              >
                {formatCurrencyAbs(r.thisMonth)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontFamily: '"Rethink Sans"',
                  fontSize: fluid(13, 0.8, 16),
                  width: 110,
                }}
              >
                {formatCurrencyAbs(r.lastMonth)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontFamily: '"Rethink Sans"',
                  fontSize: fluid(13, 0.8, 16),
                  width: 110,
                  color: getPercentColor(r.changePct),
                }}
              >
                {formatPercent(r.changePct)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>

    {total && (
      <Box sx={{ width: "100%", overflow: "hidden", mt: 0.5 }}>
        <Table size="small" sx={{ width: "100%", borderRadius: 0 }}>
          <TableBody>
            <TableRow sx={{ height: 44, "& td": { py: 0 }, border: "none" }}>
              <TableCell
                sx={{ fontWeight: 600, bgcolor: "#eef0fd", borderRadius: 0 }}
              >
                Total
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  bgcolor: "#eef0fd",
                  width: 110,
                  borderRadius: 0,
                }}
              >
                {formatCurrencyAbs(total.thisMonth)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  bgcolor: "#eef0fd",
                  width: 110,
                  borderRadius: 0,
                }}
              >
                {formatCurrencyAbs(total.lastMonth)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  bgcolor: "#eef0fd",
                  width: 110,
                  color: getPercentColor(total.changePct),
                  borderRadius: 0,
                }}
              >
                {formatPercent(total.changePct)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    )}
  </Box>
);

const VendorsList = ({ vendors = [] }) => (
  <Stack
    className="performance-vendors-list"
    spacing={0}
    sx={{ width: "100%", height: "100%", justifyContent: "flex-start" }}
  >
    {vendors.map((v, idx) => (
      <React.Fragment key={`${v.rank}-${idx}`}>
        <Stack
          className="performance-vendors-list-row"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ py: 2 }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Typography
              sx={{
                fontFamily: '"Rethink Sans"',
                fontSize: fluid(13, 0.8, 16),
                color: "#0f172b",
                width: 48,
              }}
            >
              #{v.rank}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans"',
                fontSize: fluid(13, 0.8, 16),
                color: "#0f172b",
                width: 130,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {v.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans"',
                fontSize: fluid(13, 0.8, 16),
                color: "#0f172b",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {v.category}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontFamily: '"Rethink Sans"',
              fontSize: fluid(14, 0.9, 18),
              fontWeight: 600,
              color: "rgba(23,25,35,0.9)",
              minWidth: 120,
              width: 120,
              textAlign: "right",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flexShrink: 0,
            }}
          >
            {formatCurrency(v.amount)}
          </Typography>
        </Stack>
        {idx < vendors.length - 1 && <Divider sx={{ opacity: 0.6 }} />}
      </React.Fragment>
    ))}
  </Stack>
);

const PerformanceTemplatePage = ({
  title,
  subtitle,
  description,
  badge,
  kpis = [],
  alert,
  lineChart,
  donut,
  table,
  vendors = [],
  customerId,
  metricType,
  dashboardDateStr,
  categoryDateStr,
  isYearToggled = false,
  range,
  onRangeChange,
  summary = [],
  chartData = [],
  actions,
  onBack,
  loading = false,
}) => {
  const printRef = useRef(null);
  const [exportReady, setExportReady] = useState(true);

  const handleExport = useReactToPrint({
    contentRef: printRef,
    documentTitle: title ? `${title} - Performance` : "Performance Export",
    pageStyle: `
            @page {
                size: A4 portrait;
                margin: 14mm 12mm 16mm 12mm;
            }
            @media print {
                html, body {
                    height: auto !important;
                    background: #fff !important;
                }
                body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                    font-family: "Rethink Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
                    font-size: 12px;
                    line-height: 1.35;
                }
                .performance-print-root {
                    gap: 14px !important;
                }
                .performance-print-root .MuiCard-root {
                    box-shadow: none !important;
                    border: 1px solid rgba(15, 23, 43, 0.10) !important;
                }
                canvas {
                    image-rendering: crisp-edges;
                    max-width: 100% !important;
                    height: auto !important;
                    display: block;
                }
                .performance-section-grid {
                    display: block !important;
                    overflow: visible !important;
                }
                .performance-section-grid > .performance-tile {
                    margin-bottom: 14px !important;
                }
                .performance-section-grid > .performance-tile:last-child {
                    margin-bottom: 0 !important;
                }
                .performance-tile {
                    height: auto !important;
                    overflow: visible !important;
                    min-height: 0 !important;
                    break-inside: avoid;
                    page-break-inside: avoid;
                }
                .performance-print-root .performance-tile {
                    padding: 14px !important;
                    gap: 10px !important;
                }
                .performance-print-overview {
                    break-inside: avoid;
                    page-break-inside: avoid;
                }
                .performance-print-monthly-breakdown {
                    break-inside: avoid;
                    page-break-inside: avoid;
                }
                .performance-print-category-breakdown {
                    break-inside: avoid;
                    page-break-inside: avoid;
                }
                .performance-print-monthly-breakdown > .MuiBox-root {
                    min-height: 220px !important;
                }
                .performance-print-detailed-breakdown .performance-detail-table .MuiTableCell-root {
                    font-size: 11px !important;
                    padding-top: 6px !important;
                    padding-bottom: 6px !important;
                }
                .performance-print-detailed-breakdown .performance-detail-table .MuiTableCell-head {
                    padding-top: 7px !important;
                    padding-bottom: 7px !important;
                }
                .performance-print-top-vendors .performance-vendors-list-row {
                    padding-top: 10px !important;
                    padding-bottom: 10px !important;
                }
                .performance-linechart {
                    height: 260px !important;
                    padding-bottom: 34px !important;
                    box-sizing: border-box;
                }
                .performance-linechart-axis {
                    bottom: 24px !important;
                }
                .performance-linechart-legend {
                    bottom: 6px !important;
                }
                .performance-breadcrumb {
                    display: none !important;
                }
                .performance-donut-wrap {
                    min-height: 0 !important;
                    height: auto !important;
                    max-height: none !important;
                    overflow: visible !important;
                    flex: 1 1 auto !important;
                    align-self: auto !important;
                }
                .performance-donut-chart {
                    height: auto !important;
                    min-height: 0 !important;
                    display: flex !important;
                    align-items: center !important;
                    overflow: visible !important;
                }
                .performance-donut-svg {
                    width: 160px !important;
                    height: 160px !important;
                    flex-shrink: 0 !important;
                    display: block !important;
                }
                .no-print {
                    display: none !important;
                }
            }
        `,
  });

  const exportHandler = actions?.onExport || handleExport;
  const exportDisabled = actions?.onExport ? false : !exportReady;
  const [categorySlices, setCategorySlices] = useState([]);
  const [detailRows, setDetailRows] = useState([]);
  const [detailTotal, setDetailTotal] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const safeSummary = Array.isArray(summary)
    ? summary.filter(
        (item) =>
          item &&
          typeof item === "object" &&
          item.label != null &&
          item.value != null,
      )
    : [];
  const breadcrumbLeaf =
    title?.replace(/expenses/i, "").trim() || subtitle || "Overview";

  useEffect(() => {
    if (!customerId || !metricType) return;
    const effectiveDate = dashboardDateStr;
    if (!effectiveDate) return;
    const controller = new AbortController();
    setCategoryLoading(true);
    const selectedKey = metricType === "overhead" ? "overhead" : metricType;

    const buildSectorTotals = async () => {
      const requests = CATEGORY_BREAKDOWN_SECTORS.map((sector) =>
        getAdvancedMetricSummary(
          customerId,
          effectiveDate,
          sector.key,
          controller.signal,
          { adjusted: false, year: isYearToggled },
        )
          .then((res) => {
            const data = res?.data || {};
            const actualNum = toFiniteNumber(data.actual);
            const currentMonthNum = toFiniteNumber(data.current_month_total);
            const totalSigned = isYearToggled
              ? (actualNum ?? currentMonthNum ?? 0)
              : (currentMonthNum ?? actualNum ?? 0);
            const total = Math.abs(totalSigned);
            return { key: sector.key, total };
          })
          .catch(() => ({ key: sector.key, total: 0 })),
      );

      const results = await Promise.all(requests);
      if (controller.signal.aborted) return;

      const totalsByKey = new Map(results.map((r) => [r.key, r.total]));
      const values = CATEGORY_BREAKDOWN_SECTORS.map(
        (sector) => totalsByKey.get(sector.key) || 0,
      );
      const percents = buildRoundedPercentages(values);

      const nonSelected = CATEGORY_BREAKDOWN_SECTORS.map((sector, idx) => ({
        key: sector.key,
        idx,
        percent: percents[idx],
        value: values[idx],
      }))
        .filter((row) => row.key !== selectedKey)
        .sort((a, b) => {
          if (b.percent !== a.percent) return b.percent - a.percent;
          if (b.value !== a.value) return b.value - a.value;
          return a.idx - b.idx;
        });

      const colorByKey = new Map();
      nonSelected.forEach((row, rank) => {
        colorByKey.set(
          row.key,
          CATEGORY_BREAKDOWN_GREYS[rank] ||
            CATEGORY_BREAKDOWN_GREYS[CATEGORY_BREAKDOWN_GREYS.length - 1],
        );
      });

      const slices = CATEGORY_BREAKDOWN_SECTORS.map((sector, idx) => {
        const isSelected = sector.key === selectedKey;
        return {
          key: sector.key,
          label: sector.label,
          value: values[idx],
          percent: percents[idx],
          isSelected,
          color: isSelected
            ? CATEGORY_BREAKDOWN_PRIMARY
            : colorByKey.get(sector.key) ||
              CATEGORY_BREAKDOWN_GREYS[CATEGORY_BREAKDOWN_GREYS.length - 1],
        };
      });

      const selectedSlice = slices.find((s) => s.isSelected);
      const orderedSlices = selectedSlice
        ? [selectedSlice, ...slices.filter((s) => !s.isSelected)]
        : slices;

      setCategorySlices(orderedSlices);
    };

    buildSectorTotals()
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) {
          setCategoryLoading(false);
        }
      });

    return () => controller.abort();
  }, [customerId, metricType, dashboardDateStr, categoryDateStr, isYearToggled]);

  useEffect(() => {
    if (!customerId || !metricType) return;

    const effectiveDate = dashboardDateStr;
    if (!effectiveDate) return;

    const detailType = detailTypeForMetric[metricType];
    if (!detailType) {
      setDetailRows([]);
      setDetailTotal(null);
      return;
    }

    const controller = new AbortController();

    setDetailLoading(true);

    const params = { tag: detailType, year: isYearToggled };

    getSaasVendorsAnalysis(customerId, effectiveDate, params, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;

        const vendors = res?.data?.vendors ?? [];
        const totals = res?.data?.totals ?? {};

        const rows = vendors
          .map((vendor) => ({
            category: vendor.name,
            thisMonth: toNumber(vendor.current_month_total),
            lastMonth: toNumber(vendor.previous_month_total),
            changePct: toNumber(vendor.percent_change),
          }))
          .filter((row) => row.thisMonth !== 0 || row.lastMonth !== 0);

        const total = {
          thisMonth: toNumber(totals.current_month_total),
          lastMonth: toNumber(totals.previous_month_total),
          changePct: toNumber(totals.average_percent_change),
        };

        setDetailRows(rows);
        setDetailTotal(total);
      })
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) {
          setDetailLoading(false);
        }
      });

    return () => controller.abort();
  }, [customerId, metricType, dashboardDateStr, isYearToggled]);

  const sectionCardSx = {
    bgcolor: "#fff",
    borderRadius: "12px",
    boxShadow: "0px 2px 4px 0px rgba(0,0,0,0.08)",
  };

  const tileCardSx = {
    ...sectionCardSx,
    p: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    height: { xs: 360, md: 420 },
    width: "100%",
  };

  const donutData = useMemo(() => {
    if (categorySlices.length) {
      return { ...(donut || {}), slices: categorySlices };
    }
    return donut || {};
  }, [donut, categorySlices]);

  const detailTable = useMemo(() => {
    if (detailRows.length) {
      return { ...(table || {}), rows: detailRows, total: detailTotal };
    }
    return table || {};
  }, [table, detailRows, detailTotal]);

  useEffect(() => {
    if (!printRef.current) return;

    setExportReady(false);
    let timeoutId = null;

    const finalize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setExportReady(true);
      }, 800);
    };

    const observerMutation = new MutationObserver(finalize);
    const observerResize = new ResizeObserver(finalize);

    observerMutation.observe(printRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    observerResize.observe(printRef.current);
    finalize();

    return () => {
      observerMutation.disconnect();
      observerResize.disconnect();
      clearTimeout(timeoutId);
    };
  }, [
    customerId,
    metricType,
    dashboardDateStr,
    categoryDateStr,
    loading,
    categoryLoading,
    detailLoading,
  ]);

  const sortedVendors = useMemo(() => {
    const list = Array.isArray(vendors) ? vendors : [];
    return [...list]
      .sort(
        (a, b) => Math.abs(toNumber(b?.amount)) - Math.abs(toNumber(a?.amount)),
      )
      .map((vendor, idx) => ({
        ...vendor,
        rank: idx + 1,
      }))
      .slice(0, 5);
  }, [vendors]);

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <PerformanceDriverSkeleton />
      </Box>
    );
  }

  return (
    <Box
      ref={printRef}
      className="performance-print-root"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        maxWidth: "100%",
        width: "100%",
      }}
    >
      <Stack spacing={1}>
        <Stack
          className="performance-breadcrumb"
          direction="row"
          alignItems="center"
          spacing={1.5}
        >
          <ButtonBase
            onClick={onBack}
            sx={{
              color: "rgba(0,0,0,0.87)",
              fontFamily: '"Rethink Sans"',
              fontSize: fluid(16, 1.0, 20),
              fontWeight: 400,
            }}
          >
            Performance Overview
          </ButtonBase>
          <Typography
            sx={{
              fontFamily: '"Rethink Sans"',
              fontSize: "1rem",
              color: "#9CA3AF",
              transform: "scale(2)",
              display: "inline-block",
            }}
          >
            ›
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Rethink Sans"',
              fontSize: fluid(16, 1.0, 20),
              color: "rgba(0,0,0,0.87)",
              fontWeight: 600,
            }}
          >
            {breadcrumbLeaf}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2.5}
          sx={{
            // Keep the same layout across sizes; allow horizontal scroll on smaller viewports.
            flexWrap: "nowrap",
            overflowX: { xs: "auto", md: "visible" },
            pb: { xs: 0.5, md: 0 },
          }}
        >
          <Box sx={{ minWidth: 220 }}>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans"',
                fontSize: fluid(24, 1.6, 38),
                fontWeight: 600,
                color: "#111827",
                lineHeight: 1.334,
              }}
            >
              {title}
            </Typography>
          </Box>
          <Stack
            className="no-print"
            direction="row"
            spacing={2}
            sx={{ flexWrap: "nowrap" }}
          >
            <Button
              variant="contained"
              onClick={exportHandler}
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={exportDisabled}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                py: 0.75,
                background: "#536DEC",
                fontFamily: '"Rethink Sans"',
                fontWeight: 600,
                fontSize: 14,
                lineHeight: "24px",
                letterSpacing: "0.4px",
                boxShadow:
                  "0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.20)",
                "&:hover": { background: "#4C63D7" },
              }}
            >
              {exportDisabled ? "Preparing..." : "Export"}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Divider />

      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2.5}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flexWrap: "nowrap",
            overflowX: { xs: "auto", md: "visible" },
            pb: { xs: 0.5, md: 0 },
          }}
        >
          <Card
            className="performance-print-overview"
            sx={{ ...sectionCardSx, p: "20px", width: "100%" }}
          >
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography
                  sx={{
                    fontFamily: '"Rethink Sans"',
                    fontSize: fluid(18, 1.1, 24),
                    fontWeight: 600,
                    color: "rgba(0,0,0,0.87)",
                    letterSpacing: "0.15px",
                    lineHeight: 1.6,
                  }}
                >
                  Monthly Overview
                </Typography>
                {badge && (
                  <BadgePill direction={badge.direction} text={badge.text} />
                )}
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(240px, 1fr))",
                  gap: 2.5,
                  overflowX: { xs: "auto", md: "visible" },
                  pb: { xs: 0.5, md: 0 },
                }}
              >
                {kpis.map((kpi) => (
                  <Card
                    key={kpi.label}
                    sx={{
                      bgcolor: "#fdfdfd",
                      border: "1px solid rgba(0,0,0,0.04)",
                      borderRadius: "10px",
                      boxShadow: "0px 0px 12px 0px rgba(0,0,0,0.04)",
                      height: "100%",
                    }}
                  >
                    <CardContent
                      sx={{
                        px: 2, // 16px
                        pt: 1.5, // 12px
                        pb: 1.5, // 12px
                        "&:last-child": { pb: 1.5 },
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans"',
                          fontSize: fluid(14, 0.9, 18),
                          color: "rgba(23,25,35,0.7)",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {kpi.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans"',
                          fontSize: fluid(24, 1.3, 34),
                          fontWeight: 600,
                          color: "rgba(23,25,35,0.9)",
                          letterSpacing: "0.5px",
                          lineHeight: 1.6,
                        }}
                      >
                        {kpi.value}
                      </Typography>
                      {kpi.subtext && (
                        <Typography
                          sx={{
                            fontFamily: '"Rethink Sans"',
                            fontSize: fluid(14, 0.9, 18),
                            color: kpi.subColor || "rgba(23,25,35,0.7)",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {kpi.subtext}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
              {/* {alert && <AlertStrip title={alert.title} body={alert.body} variant={alert.variant} />} */}
            </Stack>
          </Card>
        </Stack>
      </Stack>

      <Box
        className="performance-section-grid performance-section-grid--charts"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            sm: "1fr",
            md: "repeat(2, minmax(420px, 1fr))",
          },
          gap: 2.5,
          width: "100%",
          overflowX: { xs: "auto", md: "visible" },
          pb: { xs: 0.5, md: 0 },
        }}
      >
        <Card
          className="performance-tile performance-print-monthly-breakdown"
          sx={tileCardSx}
        >
          <MonthlyBreakdownChart {...lineChart} />
        </Card>
        <Card
          className="performance-tile performance-print-category-breakdown"
          sx={{ ...tileCardSx, gap: "24px" }}
        >
          <Typography
            sx={{
              fontFamily: '"Rethink Sans"',
              fontSize: fluid(18, 1.1, 24),
              fontWeight: 600,
              color: "rgba(0,0,0,0.87)",
              letterSpacing: "0.15px",
              lineHeight: 1.6,
            }}
          >
            Category Spend Breakdown
          </Typography>
          <Box
            className="performance-donut-wrap"
            sx={{ flex: 1, minHeight: 0, overflow: "visible" }}
          >
            <DonutChart {...donutData} />
          </Box>
        </Card>
      </Box>

      <Box
        className="performance-section-grid performance-section-grid--details"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            sm: "1fr",
            md: "repeat(2, minmax(420px, 1fr))",
          },
          gap: 2.5,
          width: "100%",
          overflowX: { xs: "auto", md: "visible" },
          pb: { xs: 0.5, md: 0 },
        }}
      >
        <Card
          className="performance-tile performance-print-detailed-breakdown"
          sx={{
            ...tileCardSx,
            // Web: allow cards to collapse based on content height, but
            // prevent fully collapsing to 0.
            // Mobile: keep current fixed height behavior.
            height: { xs: tileCardSx.height?.xs ?? 360, md: "min-content" },
            maxHeight: { md: 420 },
            minHeight: { md: 50 },
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Rethink Sans"',
              fontSize: fluid(18, 1.1, 24),
              fontWeight: 600,
              color: "rgba(0,0,0,0.87)",
              letterSpacing: "0.15px",
              lineHeight: 1.6,
            }}
          >
            Vendor Detailed Breakdown
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <DetailTable
              rows={detailTable?.rows || []}
              total={detailTable?.total}
            />
          </Box>
        </Card>
        <Card
          className="performance-tile performance-print-top-vendors"
          sx={{
            ...tileCardSx,
            // Web: allow cards to collapse based on content height, but
            // prevent fully collapsing to 0.
            // Mobile: keep current fixed height behavior.
            height: { xs: tileCardSx.height?.xs ?? 360, md: "stretch" },
            maxHeight: { md: 420 },
            // Keep the same baseline as Vendor Detailed Breakdown so the row
            // height is driven by whichever card needs more space.
            minHeight: { md: 50 },
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Rethink Sans"',
              fontSize: fluid(18, 1.1, 24),
              fontWeight: 600,
              color: "rgba(0,0,0,0.87)",
              letterSpacing: "0.15px",
              lineHeight: 1.6,
            }}
          >
            {metricType === "team"
              ? "Top 5 Team Transactions"
              : "Top 5 Vendors"}
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <VendorsList vendors={sortedVendors} />
          </Box>
        </Card>
      </Box>

      {safeSummary.length > 0 && (
        <Grid container spacing={1.5}>
          {safeSummary.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.label}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "0 8px 16px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  height: "100%",
                }}
              >
                <CardContent
                  sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                >
                  <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
                    {item.label}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      sx={{ color: "#111827", fontWeight: 700, fontSize: 22 }}
                    >
                      {item.value}
                    </Typography>
                    {item.delta != null && (
                      <Chip
                        size="small"
                        label={`${item.positive ? "▲" : "▼"} ${Math.abs(item.delta)}%`}
                        sx={{
                          bgcolor: item.positive
                            ? "rgba(0,176,80,0.12)"
                            : "rgba(234,88,12,0.12)",
                          color: item.positive ? "#00B050" : "#D9534F",
                          fontWeight: 700,
                          fontSize: 12,
                          height: 24,
                          borderRadius: 1.25,
                        }}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default PerformanceTemplatePage;
